/**
 * Debounced season pick saver (specs/competitions-hub-client AC-5.3).
 * Same PUT contract as the WC persistence hook (`{ predictedHomeScore,
 * predictedAwayScore }`), without the WC-specific predictions-state machinery.
 * On failure the caller reverts to the server value via onSaveError.
 *
 * Teardown mirrors `useWorldCupPickPersistence`: a pending debounce is FLUSHED on
 * unmount and on backgrounding, never just cleared. The card shows the new scoreline
 * the instant it is tapped, so dropping the timer loses a pick the user believes is
 * saved — leaving the screen within the debounce window is the common case, not an edge.
 *
 * Accepted consequence of that flush: several pending picks can complete at once, and the
 * snackbar store holds one entry, so the player sees one "Pick saved" rather than one per
 * pick. The World Cup has the same shape, and the copy is not per-match, so the report stays
 * true — it just under-counts.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, type AppStateStatus } from 'react-native';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { useSnackbarStore } from '../../shared/stores/snackbarStore';
import { COMPETITION_PICK_FEEDBACK_COPY } from '../constants/competitionPickFeedback.constants';

const SAVE_DEBOUNCE_MS = 650;

interface PendingPick {
  home: number;
  away: number;
}

interface UseSeasonPickSaverOpts {
  competitionId: string;
  /** Called after a rejected save so the caller can revert local state. */
  onSaveError: (matchId: string) => void;
}

function shouldFlushAppState(state: AppStateStatus): boolean {
  return state === 'background' || state === 'inactive';
}

export function useSeasonPickSaver({ competitionId, onSaveError }: UseSeasonPickSaverOpts) {
  const apiClient = useApiClient();
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const pendingRef = useRef<Record<string, PendingPick>>({});
  const [saveErrorMatchIds, setSaveErrorMatchIds] = useState<ReadonlySet<string>>(new Set());

  // Held in a ref so the teardown effect below can stay mount-scoped: re-subscribing
  // AppState on every competitionId/onSaveError change would drop pending flushes.
  const executeSaveRef = useRef<(matchId: string) => void>(() => {});
  useEffect(() => {
    executeSaveRef.current = (matchId: string) => {
      const pick = pendingRef.current[matchId];
      if (!pick) return;
      delete pendingRef.current[matchId];
      void (async () => {
        try {
          await apiClient(
            `/competitions/${encodeURIComponent(competitionId)}/picks/${encodeURIComponent(matchId)}`,
            { method: 'PUT', data: { predictedHomeScore: pick.home, predictedAwayScore: pick.away } },
          );
          // Fires from the SAVE's success path, not the tap, so it means "stored" rather than
          // "typed" — the card already shows the new scoreline the instant it is tapped, which
          // is exactly the confusion this removes (AC-16.1).
          useSnackbarStore.getState().showSnackbar(
            COMPETITION_PICK_FEEDBACK_COPY.PICK_SAVED,
            'success',
          );
        } catch {
          // Deliberately no snackbar. The card renders SEASON_PICKS_SAVE_ERROR inline, beside
          // the fixture that failed; a floating copy of the same news cannot name the match and
          // would just cover the badge that can (AC-16.2).
          setSaveErrorMatchIds((prev) => new Set(prev).add(matchId));
          onSaveError(matchId);
        }
      })();
    };
  }, [apiClient, competitionId, onSaveError]);

  const flushPendingRef = useRef<() => void>(() => {});
  useEffect(() => {
    flushPendingRef.current = () => {
      const timers = timersRef.current;
      for (const matchId of Object.keys(timers)) {
        clearTimeout(timers[matchId]);
        delete timers[matchId];
        executeSaveRef.current(matchId);
      }
    };
  }, []);

  useEffect(() => {
    const sub = AppState.addEventListener('change', (next: AppStateStatus) => {
      if (shouldFlushAppState(next)) flushPendingRef.current();
    });
    return () => {
      sub.remove();
      flushPendingRef.current();
    };
  }, []);

  const schedulePickSave = useCallback(
    (matchId: string, home: number, away: number) => {
      setSaveErrorMatchIds((prev) => {
        if (!prev.has(matchId)) return prev;
        const next = new Set(prev);
        next.delete(matchId);
        return next;
      });
      pendingRef.current[matchId] = { home, away };
      clearTimeout(timersRef.current[matchId]);
      timersRef.current[matchId] = setTimeout(() => {
        delete timersRef.current[matchId];
        executeSaveRef.current(matchId);
      }, SAVE_DEBOUNCE_MS);
    },
    [],
  );

  return { schedulePickSave, saveErrorMatchIds };
}
